import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { PERFIS_MVP } from "@/lib/constants";
import { CheckCircle2, XCircle, UserPlus, X, Copy, Eye, EyeOff, BarChart3, Users as UsersIcon, AlertCircle, Pencil, Check } from "lucide-react";
import ModuleHelp from "@/components/help/ModuleHelp";

const STATUS_BADGE = {
  ativo: "badge-success",
  pending_approval: "badge-warn",
  pendente: "badge-warn",
  suspenso: "badge-warn",
  rejeitado: "badge-danger",
  inativo: "badge-muted",
};
const STATUS_LABEL = {
  ativo: "Ativo",
  pending_approval: "Aguardando aprovação",
  pendente: "Pendente",
  suspenso: "Suspenso",
  rejeitado: "Rejeitado",
  inativo: "Inativo",
};
const STATUS_OPTIONS = [
  { value: "ativo", label: "Ativo" },
  { value: "pendente", label: "Pendente" },
  { value: "suspenso", label: "Suspenso" },
  { value: "inativo", label: "Inativo" },
];

const blankNew = { name: "", email: "", instituicao: "", perfil: "pesquisador", status: "ativo", password: "" };

export default function Usuarios() {
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(blankNew);
  const [creating, setCreating] = useState(false);
  const [tempPwdInfo, setTempPwdInfo] = useState(null);
  const [showPwd, setShowPwd] = useState(false);

  // Dashboard de Pesquisadores
  const [showResearchersDash, setShowResearchersDash] = useState(false);
  const [researchersData, setResearchersData] = useState(null);
  const openResearchersDash = async () => {
    setShowResearchersDash(true);
    try {
      const { data } = await api.get("/users/researchers-dashboard");
      setResearchersData(data);
    } catch (e) { setErr(formatErr(e)); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) { setErr(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const update = async (id, key, value) => {
    try {
      await api.put(`/users/${id}`, { [key]: value });
      await load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const approve = async (id) => {
    try { await api.post(`/users/${id}/approve`); await load(); }
    catch (e) { setErr(formatErr(e)); }
  };
  const reject = async (id) => {
    try { await api.post(`/users/${id}/reject`); await load(); }
    catch (e) { setErr(formatErr(e)); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setErr(""); setCreating(true); setTempPwdInfo(null);
    try {
      const { data } = await api.post("/users", form);
      if (data.temp_password) {
        setTempPwdInfo({ email: data.user.email, password: data.temp_password, name: data.user.name });
      } else {
        setShowNew(false);
      }
      setForm(blankNew);
      await load();
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setCreating(false); }
  };

  // Edição inline de e-mail
  const [editingEmail, setEditingEmail] = useState(null); // user_id em edição
  const [emailDraft, setEmailDraft] = useState("");
  const saveEmail = async (id) => {
    const value = (emailDraft || "").trim();
    try {
      await api.put(`/users/${id}`, { email: value });
      setEditingEmail(null);
      setEmailDraft("");
      await load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const pendingCount = users.filter((u) => u.status === "pending_approval").length;
  const filtered = filter === "pending" ? users.filter((u) => u.status === "pending_approval") : users;

  return (
    <div className="space-y-4" data-testid="usuarios-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mil text-[var(--gold)]">Administração</div>
          <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-3">Gestão de Usuários <ModuleHelp module="usuarios" /></h1>
          <p className="text-[var(--text-muted)] text-sm mt-2">
            {users.length} usuários · <span className="text-[var(--warn)] font-semibold">{pendingCount}</span> aguardando aprovação
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter("all")}
            className={`btn-ghost text-xs ${filter === "all" ? "border-[var(--gold)] text-[var(--gold)]" : ""}`}
            data-testid="filter-all"
          >Todos</button>
          <button
            onClick={() => setFilter("pending")}
            className={`btn-ghost text-xs ${filter === "pending" ? "border-[var(--gold)] text-[var(--gold)]" : ""}`}
            data-testid="filter-pending"
          >Pendentes ({pendingCount})</button>
          <button
            onClick={openResearchersDash}
            className="btn-ghost text-xs flex items-center gap-1"
            data-testid="btn-researchers-dashboard"
          >
            <BarChart3 size={14} /> Dashboard de Pesquisadores
          </button>
          <button
            onClick={() => { setShowNew(true); setTempPwdInfo(null); }}
            className="btn-gold text-xs flex items-center gap-1"
            data-testid="new-user-button"
          >
            <UserPlus size={14} /> Novo Usuário
          </button>
        </div>
      </div>
      {err && <div className="text-sm text-[var(--danger)]" data-testid="usuarios-error">{err}</div>}

      {/* Modal Dashboard de Pesquisadores */}
      {showResearchersDash && (
        <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4" onClick={() => setShowResearchersDash(false)}>
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-lg max-w-6xl w-full max-h-[85vh] overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="researchers-dashboard-modal">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <BarChart3 size={20} className="text-[var(--gold)]" />
                <h3 className="font-display font-bold text-lg">Dashboard de Pesquisadores</h3>
              </div>
              <button onClick={() => setShowResearchersDash(false)} className="text-[var(--text-muted)] hover:text-[var(--text)]">
                <X size={18} />
              </button>
            </div>

            {!researchersData ? (
              <div className="text-sm text-[var(--text-muted)] text-center py-6">Carregando...</div>
            ) : (
              <div className="space-y-6">
                <ResearcherSection
                  title="Pendentes de validação (importados da planilha)"
                  icon={AlertCircle}
                  color="text-[var(--warn)]"
                  items={researchersData.pending}
                  emptyMsg="Nenhum pesquisador aguardando validação."
                  testId="researchers-pending"
                />
                <ResearcherSection
                  title="Pesquisadores ativos"
                  icon={UsersIcon}
                  color="text-[var(--success)]"
                  items={researchersData.active}
                  emptyMsg="Nenhum pesquisador ativo ainda."
                  testId="researchers-active"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de criação manual */}
      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="card-mil w-full max-w-md gold-accent-top" onClick={(e) => e.stopPropagation()} data-testid="new-user-modal">
            <div className="p-5 border-b border-[var(--border)] flex items-center justify-between">
              <h2 className="font-display font-bold text-lg">Criar novo usuário</h2>
              <button onClick={() => setShowNew(false)} className="p-2 hover:bg-[var(--surface-2)] rounded text-[var(--text-muted)]" data-testid="new-user-close">
                <X size={16} />
              </button>
            </div>
            {tempPwdInfo ? (
              <div className="p-5 space-y-3" data-testid="temp-password-info">
                <div className="text-sm text-[var(--success)] font-semibold">Usuário criado com sucesso</div>
                <p className="text-xs text-[var(--text-muted)]">
                  Compartilhe a senha temporária abaixo com {tempPwdInfo.name}. Ela deve ser alterada no primeiro acesso.
                </p>
                <div className="bg-[var(--bg)] border border-[var(--gold)]/30 rounded p-3 space-y-2">
                  <div className="text-xs"><span className="label-mil">E-mail: </span><span className="font-mono">{tempPwdInfo.email}</span></div>
                  <div className="text-xs flex items-center gap-2">
                    <span className="label-mil">Senha: </span>
                    <span className="font-mono text-[var(--gold)] font-bold tracking-wider">{tempPwdInfo.password}</span>
                    <button onClick={() => navigator.clipboard.writeText(tempPwdInfo.password)} className="text-[var(--text-muted)] hover:text-[var(--gold)]">
                      <Copy size={12} />
                    </button>
                  </div>
                </div>
                <button onClick={() => { setShowNew(false); setTempPwdInfo(null); }} className="btn-gold w-full text-xs">Fechar</button>
              </div>
            ) : (
              <form onSubmit={createUser} className="p-5 space-y-3" data-testid="new-user-form">
                <div>
                  <label className="label-mil block mb-1">Nome completo *</label>
                  <input className="input-mil" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" />
                </div>
                <div>
                  <label className="label-mil block mb-1">E-mail *</label>
                  <input type="email" className="input-mil" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" />
                </div>
                <div>
                  <label className="label-mil block mb-1">Instituição</label>
                  <input className="input-mil" value={form.instituicao} onChange={(e) => setForm({ ...form, instituicao: e.target.value })} data-testid="new-user-instituicao" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-mil block mb-1">Perfil</label>
                    <select className="input-mil" value={form.perfil} onChange={(e) => setForm({ ...form, perfil: e.target.value })} data-testid="new-user-perfil">
                      {PERFIS_MVP.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label-mil block mb-1">Status</label>
                    <select className="input-mil" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="new-user-status">
                      {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="label-mil block mb-1">Senha (opcional — gerada se vazia)</label>
                  <div className="relative">
                    <input
                      type={showPwd ? "text" : "password"}
                      className="input-mil font-mono pr-10"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder="Deixe vazio para gerar senha temporária"
                      data-testid="new-user-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[var(--text-muted)] hover:text-[var(--gold)]"
                      data-testid="new-user-password-toggle"
                      title={showPwd ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={creating} className="btn-gold w-full text-xs" data-testid="new-user-submit">
                  {creating ? "CRIANDO…" : "Criar usuário"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      <div className="card-mil overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm" data-testid="usuarios-table">
            <thead>
              <tr>
                {["Usuário", "E-mail", "Instituição", "Perfil", "Status", "Criado em", "Criado por", "Ações"].map((h) => (
                  <th key={h} className="px-4 py-3 label-mil border-b border-[var(--border)] text-left whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.user_id} className="border-b border-[var(--border)]/50 hover:bg-[var(--surface-2)]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[var(--surface-2)] border border-[var(--border)] flex items-center justify-center overflow-hidden">
                        {u.foto_url ? <img src={u.foto_url} alt="" className="w-full h-full object-cover" /> :
                          <span className="font-display font-bold text-[var(--gold)] text-[10px]">{(u.name || "?").slice(0, 2).toUpperCase()}</span>}
                      </div>
                      <div className="font-semibold">{u.name}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {editingEmail === u.user_id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="email"
                          autoFocus
                          className="input-mil text-xs py-1 px-2 font-mono min-w-[220px]"
                          value={emailDraft}
                          onChange={(e) => setEmailDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveEmail(u.user_id);
                            if (e.key === "Escape") { setEditingEmail(null); setEmailDraft(""); }
                          }}
                          placeholder="email@dominio"
                          data-testid={`email-input-${u.user_id}`}
                        />
                        <button
                          onClick={() => saveEmail(u.user_id)}
                          className="p-1 text-[var(--success)] hover:bg-[var(--surface-2)] rounded"
                          title="Salvar"
                          data-testid={`email-save-${u.user_id}`}
                        >
                          <Check size={12} />
                        </button>
                        <button
                          onClick={() => { setEditingEmail(null); setEmailDraft(""); }}
                          className="p-1 text-[var(--text-muted)] hover:bg-[var(--surface-2)] rounded"
                          title="Cancelar"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 group">
                        <span className={u.email_is_placeholder || !u.email ? "italic text-[var(--text-muted)]" : ""}>
                          {u.email_is_placeholder || !u.email ? "— sem e-mail —" : u.email}
                        </span>
                        <button
                          onClick={() => { setEditingEmail(u.user_id); setEmailDraft(u.email_is_placeholder ? "" : (u.email || "")); }}
                          className="opacity-0 group-hover:opacity-100 transition p-1 text-[var(--text-muted)] hover:text-[var(--gold)] rounded"
                          title="Editar e-mail"
                          data-testid={`email-edit-${u.user_id}`}
                        >
                          <Pencil size={11} />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--text-muted)]">{u.instituicao || "—"}</td>
                  <td className="px-4 py-3">
                    <select className="input-mil text-xs py-1" value={u.perfil} onChange={(e) => update(u.user_id, "perfil", e.target.value)} data-testid={`perfil-${u.user_id}`}>
                      {PERFIS_MVP.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${STATUS_BADGE[u.status] || "badge-muted"}`} data-testid={`status-${u.user_id}`}>
                      {STATUS_LABEL[u.status] || u.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-[10px] text-[var(--text-muted)] whitespace-nowrap">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("pt-BR") : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--text-muted)] whitespace-nowrap max-w-[150px] truncate">
                    {u.created_by_admin_name || "—"}
                  </td>
                  <td className="px-4 py-3">
                    {u.status === "pending_approval" ? (
                      <div className="flex gap-1">
                        <button onClick={() => approve(u.user_id)} className="btn-ghost text-xs flex items-center gap-1 hover:!border-[var(--success)] hover:!text-[var(--success)]" data-testid={`approve-${u.user_id}`}>
                          <CheckCircle2 size={12} /> Aprovar
                        </button>
                        <button onClick={() => reject(u.user_id)} className="btn-ghost text-xs hover:!border-[var(--danger)] hover:!text-[var(--danger)]" data-testid={`reject-${u.user_id}`}>
                          <XCircle size={12} />
                        </button>
                      </div>
                    ) : u.status === "ativo" ? (
                      <div className="flex gap-1">
                        <button onClick={() => update(u.user_id, "status", "suspenso")} className="btn-ghost text-xs" data-testid={`suspend-${u.user_id}`}>Suspender</button>
                        <button onClick={() => update(u.user_id, "status", "inativo")} className="btn-ghost text-xs" data-testid={`deactivate-${u.user_id}`}>Desativar</button>
                      </div>
                    ) : (
                      <button onClick={() => approve(u.user_id)} className="btn-ghost text-xs" data-testid={`reactivate-${u.user_id}`}>Reativar</button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center p-8 text-[var(--text-muted)] text-sm">Nenhum usuário</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ResearcherSection({ title, icon: Icon, color, items, emptyMsg, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} className={color} />
        <h4 className="font-display font-semibold text-sm">{title}</h4>
        <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-[var(--text-muted)] italic px-4 py-3 bg-[var(--surface-2)] rounded">
          {emptyMsg}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-2)]">
              <tr>
                <th className="px-3 py-2 label-mil text-left">Pesquisador</th>
                <th className="px-3 py-2 label-mil text-left">E-mail</th>
                <th className="px-3 py-2 label-mil text-right">Fontes</th>
                <th className="px-3 py-2 label-mil text-right">Sementes</th>
                <th className="px-3 py-2 label-mil text-right">Aprovadas</th>
                <th className="px-3 py-2 label-mil text-right">Pendentes</th>
                <th className="px-3 py-2 label-mil text-right">Aguardando IA</th>
                <th className="px-3 py-2 label-mil text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.user_id} className="border-b border-[var(--border)]/50" data-testid={`researcher-row-${r.user_id}`}>
                  <td className="px-3 py-2 font-display font-semibold">{r.name}</td>
                  <td className="px-3 py-2 text-xs text-[var(--text-muted)]">
                    {r.email_is_placeholder ? <em className="opacity-60">sem e-mail</em> : (r.email || "—")}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{r.totals.sources}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.totals.seeds}</td>
                  <td className="px-3 py-2 text-right font-mono text-[var(--success)]">{r.totals.approved}</td>
                  <td className="px-3 py-2 text-right font-mono text-[var(--warn)]">{r.totals.pending_review}</td>
                  <td className="px-3 py-2 text-right font-mono text-[var(--gold)]">{r.totals.pending_enrichment}</td>
                  <td className="px-3 py-2">
                    {r.requires_admin_validation ? (
                      <span className="badge badge-warn text-[10px]">Aguardando validação</span>
                    ) : r.active ? (
                      <span className="badge badge-success text-[10px]">Ativo</span>
                    ) : (
                      <span className="badge badge-muted text-[10px]">Inativo</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
